/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import React, { useState, useEffect } from "react";
import { Canceler } from "axios";
import omitBy from "lodash-es/omitBy";
//context
import { useProgressValue } from "../context";
//custom libraries

import {
  FORM_FIELD_ORDER_DATE_FROM,
  FORM_FIELD_ORDER_DATE_TO,
  FORM_FIELD_ORDER_ID,
  FORM_FIELD_PHONE,
  LIMIT,
  ZERO_OFFSET,
} from "../constants/common";

import {
  ORDERID,
  ORDERTIME,
  LOGINID,
  STATUS,
  SORT_ORDER_NUMBER_ASC,
  SORT_ORDER_NUMBER_DESC,
  SORT_ORDER_DATE_ASC,
  SORT_ORDER_DATE_DESC,
  SORT_LOGONID_ASC,
  SORT_LOGONID_DESC,
  SORT_STATUS_ASC,
  SORT_STATUS_DESC,
} from "../constants/order";

import { useSalesValue } from "./../context/sales-context";
import { useParamsSalesValue } from "./../context/sale-search-context";
import { useSaleHandler } from "./rest/use-sale-handler";
import { getPreviousDay } from "./../helpers/date";

export interface ParamsJson {
  limit?: number;
  offset?: number;
  [FORM_FIELD_ORDER_ID]?: string;
  findBy?: string;
  logonId?: string;
  logonIdSearchType?: string;
  orderByTableName?: string;
  orderByFieldName?: string;
  firstName?: string;
  firstNameSearchType?: string;
  lastName?: string;
  lastNameSearchType?: string;
  streetAddress1?: string;
  streetAddress1SearchType?: string;
  city?: string;
  citySearchType?: string;
  zipCode?: string;
  zipCodeSearchType?: string;
  country?: string;
  countrySearchType?: string;
  state?: string;
  stateSearchType?: string;
  email?: string;
  emailSearchType?: string;
  [FORM_FIELD_PHONE]?: string;
  phoneSearchType?: string;
  [FORM_FIELD_ORDER_DATE_FROM]?: string;
  [FORM_FIELD_ORDER_DATE_TO]?: string;
  orderDateSearchType?: string;
  addressType?: string;
  parentOrgNameSearchType?: string;
  parentOrgName?: string;
  groupBy?: String;
}

export const initParams: ParamsJson = {
  [FORM_FIELD_ORDER_DATE_FROM]: "",
  [FORM_FIELD_ORDER_DATE_TO]: "",
  groupBy: "DAY",
};

export const useSaleForm = () => {
  const [listRecords, setListRecords] = useState([]);
  const { setInProgress } = useProgressValue();
  const { findSales } = useSaleHandler();

  const {
    sales,
    setSales,
    graphMode,
    setGraphMode,
    comparativeData,
    setComparativeData,
    avgCom,
    avgApp,
    setAvgApp,
    setAvgCom,
    pagination,
    setPagination,
  } = useSalesValue();
  const { params, setParams } = useParamsSalesValue();
  const [chips, setChips] = React.useState<any>({});
  const [showForm, setShowForm] = React.useState<boolean>(true);

  const [formData, setFormData] = React.useState<ParamsJson | any>(params);
  const [sortOrder, setSortOrder] = useState<string>(() => "asc");
  const [orderBy, setOrderBy] = useState<string>(() => "");
  const [sortParam, setSortParam] = useState<any>();

  const getPrev = () => {
    setPagination({
      ...pagination,
      pageNumber: pagination.pageNumber - 1,
      records: paginate(sales.resultList, LIMIT, pagination.pageNumber - 1),
    });
  };

  const getNext = () => {
    setPagination({
      ...pagination,
      pageNumber: pagination.pageNumber + 1,
      records: paginate(sales.resultList, LIMIT, pagination.pageNumber + 1),
    });
  };

  const handleChangeGroupBy = (val: string) => {
    setParams({ ...params, groupBy: val });
    const evt = params;
    evt.orderDateFrom = `${evt.orderDateFrom}`.replace(" 00:00:00", "");
    evt.orderDateTo = `${evt.orderDateTo}`.replace(" 23:59:59", "");
    submitSaleForm({ ...params, ...evt, groupBy: val });
  };

  const handleGetPrevDayData = (val: string) => {
    let initDate = params.orderDateFrom;

    let yesterday = getPreviousDay(new Date(initDate));

    setParams({
      ...params,
      prevDay: `${yesterday.toISOString().substring(0, 10)} 00:00:00`,
    });
    let data = {
      orderDateFrom: `${yesterday.toISOString().substring(0, 10)} 00:00:00`,
      orderDateTo: params.orderDateTo,
      groupBy: "HOUR",
    };
    const [res, canceler] = findSales(data, ZERO_OFFSET, LIMIT);
    res.then((r) => {
      setComparativeData(r.data);
      console.log(r.data);
    });
    //submitSaleForm({orderDateFrom:yesterday})
  };

  const submitSaleForm = (evt: any) => {
    /*     setSales({});
    setFormData(evt);*/
    setAvgApp([]);
    setAvgCom([]);

    evt.orderDateFrom = `${evt.orderDateFrom} 00:00:00`;
    evt.orderDateTo = `${evt.orderDateTo} 23:59:59`;

    const { busChannel, ...newParams } = params;
    setParams({ ...newParams, ...evt });

    const chips = omitBy({ ...params, ...evt }, (s) => String(s).trim() === "");
    if (!evt.busChannel) delete chips["busChannel"];

    const [res, canceler] = findSales(chips, ZERO_OFFSET);
    cancels.cancels = canceler;

    res.then((r) => {
      let countResults = r.data.recordSetTotal;

      if (!evt.busChannel) {
        //.com
        const [res1] = findSales(
          { ...chips, busChannel: -1 },
          ZERO_OFFSET,
          countResults
        );

        res1
          .then((r) => r.data)
          .then((data) => {
            setAvgCom(data.resultList);
          });

        //app
        const [res2] = findSales(
          { ...chips, busChannel: -6 },
          ZERO_OFFSET,
          countResults
        );

        res2
          .then((r) => r.data)
          .then((data) => {
            setAvgApp(data.resultList);
          });
      } else {
        //.com
        if (evt.busChannel === -1) {
          setAvgCom(r.data.resultList);

          const [res] = findSales(
            { ...chips, busChannel: -6 },
            ZERO_OFFSET,
            countResults
          );

          res
            .then((r) => r.data)
            .then((data) => {
              setAvgApp(data.resultList);
            });
        }
        //app
        if (evt.busChannel === -6) {
          setAvgApp(r.data.resultList);

          const [res] = findSales(
            { ...chips, busChannel: -1 },
            ZERO_OFFSET,
            countResults
          );

          res
            .then((r) => r.data)
            .then((data) => {
              setAvgCom(data.resultList);
            });
        }
      }
      if (countResults > 0) {
        console.log(chips);
        setListRecords(r.data.resultList);
      } else {
        setListRecords([]);
      }
    });

    getSales(chips, ZERO_OFFSET);
  };

  const clickSearchSort = () => {
    getSales(sortParam, ZERO_OFFSET, false);
  };

  const sortAction = (props: string) => {
    if (orderBy === props && sortOrder === "asc") {
      setSortOrder("desc");
    } else if (orderBy === props && sortOrder === "desc") {
      setSortOrder("asc");
    } else if (orderBy !== props && sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortOrder("asc");
    }
    setOrderBy(props);
  };

  const removeChip = (name: string) => {
    const newParams = { ...params, [name]: "" };
    getSales(newParams, ZERO_OFFSET);
    setParams(newParams);
  };

  const cancels: { cancels: any[] } = { cancels: [] as any[] };

  const getOrder = (id: string) => {
    const [res, canceler] = findSales({ orderId: id }, ZERO_OFFSET, LIMIT);
    cancels.cancels = canceler;

    res.then((r) => {
      const newOrderItems = sales.items.map((o: any) => {
        if (o.orderId === r.data.items[0].orderId) {
          return Object.assign({ ...o }, r.data.items[0]);
        } else {
          return { ...o };
        }
      });

      setSales({ ...sales, items: newOrderItems });
    });
  };

  const getSales = (param: any, newOffset: number, setChip = true) => {
    const chips = omitBy(param, (s) => String(s).trim() === "");
    let searchParams = {};
    if (chips["orderByTableName"] || chips["orderByFieldName"]) {
      const existingParams = omitBy(params, (s) => String(s).trim() === "");
      searchParams = { ...existingParams, ...chips };
    } else searchParams = { ...chips };
    setInProgress(true);
    const [res, canceler] = findSales(searchParams, newOffset, LIMIT);
    cancels.cancels = canceler;
    return res
      .then((r) => r.data)
      .then((data) => {
        console.log(data);
        if (setChip) {
          setChips(chips);
          setOrderBy("");
          setSortParam("");
        }

        console.log("datSet page number + 1");
        setSales({ ...data, pageNumber: data.pageNumber + 1 });
        setPagination({
          ...pagination,
          total: data.recordSetTotal,
          records: paginate(data.resultList, LIMIT, 1),
          numberPages: getPagesNumber(data.resultList),
        });
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });
  };

  useEffect(() => {
    console.log(params);
    const chips = omitBy(params, (s) => String(s).trim() === "");
    setChips(chips);
    const keys = Object.keys(chips);
    sales.count && keys.length && setShowForm(false);
    return () => {
      cancels.cancels.forEach((cancel: Canceler) => {
        return cancel();
      });
    };
  }, []);

  useEffect(() => {
    if (orderBy) {
      switch (orderBy) {
        case ORDERID:
          setSortParam(
            sortOrder === "asc" ? SORT_ORDER_NUMBER_ASC : SORT_ORDER_NUMBER_DESC
          );
          break;

        case ORDERTIME:
          setSortParam(
            sortOrder === "asc" ? SORT_ORDER_DATE_ASC : SORT_ORDER_DATE_DESC
          );
          break;

        case LOGINID:
          setSortParam(
            sortOrder === "asc" ? SORT_LOGONID_ASC : SORT_LOGONID_DESC
          );
          break;

        case STATUS:
          setSortParam(
            sortOrder === "asc" ? SORT_STATUS_ASC : SORT_STATUS_DESC
          );
          break;

        default:
          setSortParam("");
      }
    }
  }, [orderBy, sortOrder]);

  useEffect(() => {
    if (sortParam) {
      clickSearchSort();
    }
  }, [sortParam]);

  return {
    /*     changeInput, */
    listRecords,
    submitSaleForm,
    sales,
    params,
    formData,
    removeChip,
    chips,
    getOrder,
    pagination,
    getPrev,
    getNext,

    showForm,
    setShowForm,
    sortAction,
    sortOrder,
    orderBy,

    handleChangeGroupBy,

    graphMode,
    setGraphMode,

    handleGetPrevDayData,

    comparativeData,

    avgApp,
    avgCom,
  };
};
function paginate(array: any, page_size: number, page_number: number) {
  // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
  return array.slice((page_number - 1) * page_size, page_number * page_size);
}
function getPagesNumber(array: any) {
  return Math.ceil(array.length / LIMIT);
}
